/**
 * EmployeeSummary.jsx — Personel Özet sayfası (SUPER_ADMIN).
 *
 * Şub 09, 2026 — İş görüşmeleri / performans değerlendirmesi için kullanılır.
 * TR karakter destekli arama + 5 hazır tarih aralığı + 7 başlık altında özet.
 */
import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, Search, User, Calendar, Truck, Package, Wallet, Activity, Briefcase, Printer, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Money } from '../components/Money';

const formatLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const STATUS_LABELS = {
  WORKING: 'Çalıştı',
  HALF_DAY: 'Yarım gün',
  SICK: 'Hasta',
  DOP: 'DOP',
  ON_LEAVE: 'İzinli',
  PATERNITY: 'Babalık izni',
};

const RANGE_PRESETS = [
  { key: 'thisMonth', label: 'Bu Ay' },
  { key: 'lastMonth', label: 'Geçen Ay' },
  { key: 'thisYear', label: 'Bu Yıl' },
  { key: 'lastYear', label: 'Geçen Yıl' },
  { key: 'custom', label: 'Tarih Aralığı' },
];

function computeRange(preset, customFrom, customTo) {
  const today = new Date();
  if (preset === 'thisMonth') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start: formatLocalDate(start), end: formatLocalDate(today) };
  }
  if (preset === 'lastMonth') {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: formatLocalDate(start), end: formatLocalDate(end) };
  }
  if (preset === 'thisYear') {
    return { start: `${today.getFullYear()}-01-01`, end: formatLocalDate(today) };
  }
  if (preset === 'lastYear') {
    const y = today.getFullYear() - 1;
    return { start: `${y}-01-01`, end: `${y}-12-31` };
  }
  return { start: customFrom || '', end: customTo || '' };
}

export default function EmployeeSummary() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);

  const [preset, setPreset] = useState('thisYear');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const range = useMemo(() => computeRange(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [detailKey, setDetailKey] = useState(null); // hangi kart detay panelini açtı

  // Search
  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/admin/employee-summary/search?q=${encodeURIComponent(query)}`);
        if (!cancelled) setResults(r.data?.data || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  // Summary fetch on selection or range change
  useEffect(() => {
    if (!selected?.id) return;
    let cancelled = false;
    (async () => {
      setSummaryLoading(true);
      try {
        const params = new URLSearchParams();
        if (range.start) params.set('start_date', range.start);
        if (range.end) params.set('end_date', range.end);
        const r = await api.get(`/admin/employee-summary/${selected.id}?${params.toString()}`);
        if (!cancelled) setSummary(r.data?.data || null);
      } catch {
        if (!cancelled) setSummary(null);
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected?.id, range.start, range.end]);

  return (
    <div className="space-y-5" data-testid="employee-summary-page">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #employee-summary-print, #employee-summary-print * { visibility: visible !important; }
          #employee-summary-print { position: absolute; left: 0; top: 0; width: 100%; padding: 1rem; }
          .no-print { display: none !important; }
          /* Şub 10, 2026 — Print modunda detayları her zaman göster */
          .print-only { display: block !important; }
          .print-only-section { page-break-inside: avoid; margin-top: 1rem; }
          .print-only-section h3 { font-size: 14px; font-weight: bold; margin-bottom: 6px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
          .print-only-section table { width: 100%; border-collapse: collapse; font-size: 11px; }
          .print-only-section th, .print-only-section td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
          .print-only-section th { background: #f3f4f6; font-weight: 600; }
          @page { margin: 12mm; }
        }
        .print-only { display: none; }
      `}</style>

      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Personel Özet</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            İş görüşmeleri ve performans değerlendirmesi için toplulaştırılmış çalışan raporu.
          </p>
        </div>
        {summary && (
          <Button variant="outline" size="sm" onClick={() => window.print()} className="no-print gap-1.5" data-testid="emp-sum-print">
            <Printer className="h-4 w-4" /> PDF Olarak İndir
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="no-print">
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Çalışan ara"
              className="pl-9 h-10"
              data-testid="emp-sum-search-input"
              autoFocus
            />
          </div>
          {searching && (
            <div className="text-xs text-slate-400 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Aranıyor…
            </div>
          )}
          {results.length > 0 && (
            <div className="max-h-60 overflow-y-auto border rounded-lg divide-y dark:divide-slate-700">
              {results.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => { setSelected(u); setQuery(u.full_name || ''); setResults([]); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between gap-2"
                  data-testid={`emp-sum-result-${u.id}`}
                >
                  <div>
                    <div className="font-medium text-sm">{u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim()}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Date range presets */}
      <Card className="no-print">
        <CardContent className="p-4 flex flex-wrap items-center gap-2">
          <Calendar className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Aralık:</span>
          {RANGE_PRESETS.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={preset === p.key ? 'default' : 'outline'}
              onClick={() => setPreset(p.key)}
              data-testid={`emp-sum-range-${p.key}`}
            >
              {p.label}
            </Button>
          ))}
          {preset === 'custom' && (
            <div className="flex items-center gap-2 ml-2">
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-40 h-8" />
              <span className="text-slate-400">-</span>
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-40 h-8" />
            </div>
          )}
          {range.start && range.end && (
            <span className="ml-auto text-xs text-slate-500">{range.start} → {range.end}</span>
          )}
        </CardContent>
      </Card>

      {!selected && (
        <Card><CardContent className="py-12 text-center text-slate-400">
          <User className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p>Başlamak için yukarıdan bir çalışan arayın.</p>
        </CardContent></Card>
      )}

      {selected && summaryLoading && (
        <Card><CardContent className="py-12 text-center"><Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" /></CardContent></Card>
      )}

      {selected && summary && (
        <div id="employee-summary-print" className="space-y-5">
          {/* Şub 10, 2026 — PDF/Print-only: firma logo + adı bandı (sadece print modunda görünür) */}
          {summary.firm && (
            <div
              className="print-only"
              style={{
                display: 'none',
                borderBottom: `3px solid ${summary.firm.primary_color || '#1A3A6B'}`,
                paddingBottom: '10px',
                marginBottom: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                {summary.firm.logo_url && (
                  <img
                    src={summary.firm.logo_url}
                    alt={summary.firm.name}
                    style={{ height: '52px', width: '52px', objectFit: 'contain' }}
                  />
                )}
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: summary.firm.primary_color || '#1A3A6B' }}>
                    {summary.firm.name || 'OMS Portal'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#666' }}>Personel Özet Raporu</div>
                </div>
              </div>
            </div>
          )}

          {/* Profile header */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-xl font-bold">{summary.profile.full_name || `${summary.profile.first_name || ''} ${summary.profile.last_name || ''}`.trim()}</h2>
                  <div className="text-sm text-slate-600 dark:text-slate-300 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    {summary.profile.email && <span><b>E-posta:</b> {summary.profile.email}</span>}
                    {summary.profile.phone && <span><b>GSM:</b> {summary.profile.phone}</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-slate-600 dark:text-slate-300">
                    {summary.profile.hire_date && <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3" /><b>İşe başlama:</b> {summary.profile.hire_date}</span>}
                    {summary.profile.birth_date && <span><b>Doğum tarihi:</b> {summary.profile.birth_date}</span>}
                    {summary.profile.nationality && <span><b>Uyruk:</b> {summary.profile.nationality}</span>}
                    {summary.profile.national_id && <span><b>Kimlik:</b> {summary.profile.national_id}</span>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <Badge variant="outline">{summary.profile.role}</Badge>
                  <Badge variant={summary.profile.is_active ? 'default' : 'secondary'}>{summary.profile.is_active ? 'Aktif' : 'Pasif'}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stat grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard onClick={() => setDetailKey('work')} icon={Activity} title="Çalışma Günleri" value={summary.work_days.total_recorded_days} sub={
              Object.entries(summary.work_days.by_status).map(([k, v]) => `${STATUS_LABELS[k] || k}: ${v}`).join(' · ') || '—'
            } accent="emerald" />
            <StatCard onClick={() => setDetailKey('leave')} icon={Calendar} title="Onaylı İzin (gün)" value={summary.leave.total_days} sub={`${summary.leave.request_count} talep`} accent="amber" />
            <StatCard onClick={() => setDetailKey('routes')} icon={Truck} title="Çalışılan Tur" value={summary.routes.count} sub={
              Object.entries(summary.routes.by_customer).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'
            } accent="indigo" />
            <StatCard onClick={() => setDetailKey('routes')} icon={Truck} title="Toplam KM" value={summary.routes.total_km.toLocaleString('tr-TR')} sub="(turlardan)" accent="cyan" />
            <StatCard onClick={() => setDetailKey('packages')} icon={Package} title="PostNL Paket" value={summary.packages.total_stops.toLocaleString('tr-TR')} sub={`${summary.packages.active_days} aktif gün · ort. ${summary.packages.avg_per_day}/gün`} accent="orange" />
            <StatCard onClick={() => setDetailKey('loonbriefs')} icon={Wallet} title="Bordro" value={summary.loonbriefs.count} sub={<>Brüt: <Money amount={summary.loonbriefs.gross_total} /> · Net: <Money amount={summary.loonbriefs.net_total} /></>} accent="violet" />
            <StatCard onClick={() => setDetailKey('finance')} icon={Wallet} title="Finansal Kayıt" value={summary.finance.count} sub={
              Object.entries(summary.finance.by_type).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'
            } accent="rose" />
            <StatCard icon={Activity} title="Aralık" value={`${summary.date_range.start || '—'}`} sub={`→ ${summary.date_range.end || '—'}`} accent="slate" />
          </div>

          {/* Detail panel — tıklanan karta göre liste */}
          {detailKey && (
            <DetailPanel
              kind={detailKey}
              summary={summary}
              userId={selected.id}
              onClose={() => setDetailKey(null)}
            />
          )}

          {/* Tur detayları (müşteri bazlı) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Tur Dağılımı</CardTitle>
            </CardHeader>
            <CardContent>
              {summary.routes.count === 0 ? (
                <p className="text-slate-400 text-sm">Bu aralıkta tur kaydı yok.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  {Object.entries(summary.routes.by_customer).map(([c, n]) => (
                    <div key={c} className="border rounded-lg p-2 flex items-center justify-between">
                      <span className="capitalize">{c}</span>
                      <Badge variant="secondary">{n}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Şub 10, 2026 — PDF/Print-only: tüm detay tabloları */}
          <PrintOnlyDetails summary={summary} />
        </div>
      )}
    </div>
  );
}

function PrintOnlyDetails({ summary }) {
  const fmtMoney = (n) => `€${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
  return (
    <div className="print-only">
      <div className="print-only-section">
        <h3>Çalışma Günleri ({summary.work_days.total_recorded_days})</h3>
        {(summary.work_days.entries || []).length === 0 ? <p>Kayıt yok.</p> : (
          <table>
            <thead><tr><th>Tarih</th><th>Durum</th><th>Açıklama</th></tr></thead>
            <tbody>
              {(summary.work_days.entries || []).map((e, i) => (
                <tr key={i}>
                  <td>{e.work_date || e.date || '-'}</td>
                  <td>{STATUS_LABELS[e.status] || e.status || '-'}</td>
                  <td>{e.description || e.note || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="print-only-section">
        <h3>Onaylı İzinler ({summary.leave.request_count} talep / {summary.leave.total_days} gün)</h3>
        {(summary.leave.entries || []).length === 0 ? <p>Kayıt yok.</p> : (
          <table>
            <thead><tr><th>Tür</th><th>Başlangıç</th><th>Bitiş</th><th>Gün</th><th>Açıklama</th></tr></thead>
            <tbody>
              {summary.leave.entries.map((e, i) => (
                <tr key={i}>
                  <td>{e.leave_type_code || '-'}</td>
                  <td>{e.start_date || '-'}</td>
                  <td>{e.end_date || '-'}</td>
                  <td>{e.days || 0}</td>
                  <td>{e.description || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="print-only-section">
        <h3>Çalışılan Turlar ({summary.routes.count} / Toplam {summary.routes.total_km.toLocaleString('tr-TR')} km)</h3>
        {(summary.routes.entries || []).length === 0 ? <p>Kayıt yok.</p> : (
          <table>
            <thead><tr><th>Tarih</th><th>Müşteri</th><th>Rit No</th><th>KM</th></tr></thead>
            <tbody>
              {summary.routes.entries.map((e, i) => (
                <tr key={i}>
                  <td>{e.date || '-'}</td>
                  <td>{e.customer || '-'}</td>
                  <td>{e.rit_number || '-'}</td>
                  <td>{e.km_total || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="print-only-section">
        <h3>PostNL Paket Kayıtları ({summary.packages.total_stops.toLocaleString('tr-TR')} paket / {summary.packages.active_days} aktif gün)</h3>
        {(summary.packages.entries || []).length === 0 ? <p>Kayıt yok.</p> : (
          <table>
            <thead><tr><th>Tarih</th><th>Bölge</th><th>Miktar</th></tr></thead>
            <tbody>
              {summary.packages.entries.map((e, i) => (
                <tr key={i}>
                  <td>{e.work_date || '-'}</td>
                  <td>{e.region || '-'}</td>
                  <td>{e.quantity || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="print-only-section">
        <h3>Bordrolar ({summary.loonbriefs.count} · Brüt {fmtMoney(summary.loonbriefs.gross_total)} · Net {fmtMoney(summary.loonbriefs.net_total)})</h3>
        {(summary.loonbriefs.entries || []).length === 0 ? <p>Kayıt yok.</p> : (
          <table>
            <thead><tr><th>Dosya</th><th>Tarih</th><th>Tutar</th></tr></thead>
            <tbody>
              {summary.loonbriefs.entries.map((e, i) => (
                <tr key={i}>
                  <td>{e.file_name || '-'}</td>
                  <td>{(e.created_at || '').slice(0, 10)}</td>
                  <td>{e.amount ? fmtMoney(e.amount) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="print-only-section">
        <h3>Finansal Kayıtlar ({summary.finance.count})</h3>
        {(summary.finance.entries || []).length === 0 ? <p>Kayıt yok.</p> : (
          <table>
            <thead><tr><th>Tarih</th><th>Tür</th><th>Tutar</th><th>Açıklama</th></tr></thead>
            <tbody>
              {summary.finance.entries.map((e, i) => {
                const positive = ['pot_plus', 'laadvergoeding', 'odeme', 'prim'].includes(e.entry_type);
                return (
                  <tr key={i}>
                    <td>{e.date || '-'}</td>
                    <td>{e.entry_type || '-'}</td>
                    <td>{positive ? '+' : '-'}{fmtMoney(e.amount)}</td>
                    <td>{e.description || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="print-only-section" style={{ marginTop: '20px', fontSize: '10px', color: '#666' }}>
        Rapor aralığı: {summary.date_range.start || '—'} → {summary.date_range.end || '—'} ·
        Oluşturulma: {new Date().toLocaleString('tr-TR')}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, title, value, sub, accent, onClick }) {
  const accentClasses = {
    emerald: 'border-l-emerald-500',
    amber: 'border-l-amber-500',
    indigo: 'border-l-indigo-500',
    cyan: 'border-l-cyan-500',
    orange: 'border-l-orange-500',
    violet: 'border-l-violet-500',
    rose: 'border-l-rose-500',
    slate: 'border-l-slate-400',
  };
  return (
    <Card
      className={`border-l-4 ${accentClasses[accent] || ''} ${onClick ? 'cursor-pointer hover:shadow-md transition' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
          <Icon className="h-3.5 w-3.5" />
          {title}
        </div>
        <div className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-1">{value}</div>
        <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">{sub}</div>
      </CardContent>
    </Card>
  );
}

// Şub 09, 2026 — Karta tıklayınca açılan detay paneli.
function DetailPanel({ kind, summary, userId, onClose }) {
  const data = summary[kind];
  const entries = data?.entries || [];
  const navigate = useNavigate();
  const titles = {
    work: 'Çalışma Günleri',
    leave: 'Onaylı İzinler',
    routes: 'Çalışılan Turlar',
    packages: 'PostNL Paket Detayları',
    loonbriefs: 'Bordrolar',
    finance: 'Finansal Kayıtlar',
  };
  // "Çalışan Profilinde Aç" hedef tab eşlemesi
  const profileTabMap = {
    leave: { url: `/dashboard/employees/${userId}`, label: 'Profilde Aç' },
    routes: { url: `/dashboard/routes`, label: 'Tur Listesinde Aç' },
    packages: { url: `/dashboard/employees/${userId}?tab=packages`, label: 'Paketler Tabı' },
    loonbriefs: { url: `/dashboard/employees/${userId}?tab=loonbriefs`, label: 'Bordrolar Tabı' },
    finance: { url: `/dashboard/employees/${userId}?tab=finance`, label: 'Finans Tabı' },
    work: { url: `/dashboard/employees/${userId}?tab=history`, label: 'Geçmiş Tabı' },
  };
  const target = profileTabMap[kind];
  return (
    <Card className="border-blue-200 dark:border-blue-800/60" data-testid={`emp-sum-detail-${kind}`}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base">{titles[kind]}{entries.length ? ` (${entries.length})` : ''}</CardTitle>
        <div className="flex items-center gap-1.5">
          {target && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(target.url)}
              className="h-7 gap-1 text-xs"
              data-testid={`emp-sum-open-${kind}`}
            >
              <ExternalLink className="h-3 w-3" /> {target.label}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onClose} className="h-7 px-2 text-xs">Kapat ✕</Button>
        </div>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-slate-400 text-sm">Bu aralıkta kayıt yok.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto border rounded-lg divide-y dark:divide-slate-700">
            {entries.map((e, i) => <DetailRow key={i} kind={kind} entry={e} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DetailRow({ kind, entry }) {
  if (kind === 'leave') {
    return (
      <div className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
        <div>
          <div className="font-medium">{entry.leave_type_code} · <span className="text-slate-500">{entry.days} gün</span></div>
          <div className="text-xs text-slate-500">{entry.start_date} → {entry.end_date}</div>
          {entry.description && <div className="text-xs text-slate-500 italic mt-0.5">{entry.description}</div>}
        </div>
      </div>
    );
  }
  if (kind === 'routes') {
    return (
      <div className="px-3 py-2 flex items-center justify-between text-sm">
        <div>
          <div className="font-medium capitalize">{entry.customer || '-'}{entry.rit_number ? ` · ${entry.rit_number}` : ''}</div>
          <div className="text-xs text-slate-500">{entry.date}</div>
        </div>
        <Badge variant="secondary">{entry.km_total || 0} km</Badge>
      </div>
    );
  }
  if (kind === 'packages') {
    return (
      <div className="px-3 py-2 flex items-center justify-between text-sm">
        <div>
          <div className="font-medium">{entry.work_date}</div>
          {entry.region && <div className="text-xs text-slate-500">{entry.region}</div>}
        </div>
        <Badge variant="secondary">{entry.quantity || 0} paket</Badge>
      </div>
    );
  }
  if (kind === 'loonbriefs') {
    return (
      <div className="px-3 py-2 flex items-center justify-between text-sm">
        <div>
          <div className="font-medium">{entry.file_name || '-'}</div>
          <div className="text-xs text-slate-500">{(entry.created_at || '').slice(0, 10)}</div>
        </div>
        {entry.amount ? <Badge variant="secondary"><Money amount={entry.amount} revealKey={`loonbrief-${entry.file_name || entry.id || ''}`} /></Badge> : null}
      </div>
    );
  }
  if (kind === 'finance') {
    const positive = ['pot_plus', 'laadvergoeding', 'odeme', 'prim'].includes(entry.entry_type);
    return (
      <div className="px-3 py-2 flex items-center justify-between text-sm">
        <div>
          <div className="font-medium">{entry.entry_type} · <span className={positive ? 'text-emerald-600' : 'text-rose-600'}><Money amount={entry.amount} prefix={positive ? '+' : '-'} revealKey={`fin-${entry.id || entry.date || ''}`} /></span></div>
          <div className="text-xs text-slate-500">{entry.date}{entry.description ? ` · ${entry.description}` : ''}</div>
        </div>
      </div>
    );
  }
  return <div className="px-3 py-2 text-xs text-slate-500">{JSON.stringify(entry)}</div>;
}
